import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  Radio, Video, ListVideo, PlaySquare, Rss, BarChart3,
  CreditCard, Activity, Users, Layers, UploadCloud, HardDrive, Cloud, Church, Inbox, LifeBuoy, Tv, Target, MonitorPlay,
} from "lucide-react";

const GROUPS = [
  {
    name: "MEDIA",
    items: [
      { label: "Videos", icon: Video, path: "/videos" },
      { label: "Streams", icon: Radio, path: "/streams" },
      { label: "Playlists", icon: ListVideo, path: "/playlists" },
      { label: "Players", icon: PlaySquare, path: "/players" },
      { label: "Feeds", icon: Rss, path: "/feeds" },
    ],
  },
  {
    name: "ANALYTICS",
    items: [
      { label: "Analytics", icon: BarChart3, path: "/analytics" },
    ],
  },
  {
    name: "ACCOUNT",
    items: [
      { label: "Church Profile", icon: Church, path: "/profile", churchOnly: true },
      { label: "Billing", icon: CreditCard, path: "/billing" },
      { label: "Usage", icon: Activity, path: "/usage" },
      { label: "Support", icon: LifeBuoy, path: "/support" },
      { label: "Clients", icon: Users, path: "/team", adminOnly: true },
      { label: "Support Tickets", icon: LifeBuoy, path: "/support-tickets", adminOnly: true },
      { label: "Subscriptions", icon: CreditCard, path: "/subscriptions", adminOnly: true },
      { label: "Support Inbox", icon: Inbox, path: "/support-inbox", adminOnly: true },
      { label: "Church Branding", icon: Church, path: "/church-branding", adminOnly: true },
      { label: "Sub-Accounts", icon: Layers, path: "/sub-accounts", adminOnly: true },
      { label: "Upload Profiles", icon: UploadCloud, path: "/upload-profiles", adminOnly: true },
      { label: "Storage", icon: HardDrive, path: "/storage", adminOnly: true },
      { label: "Channel Builder", icon: Tv, path: "/channel-builder", adminOnly: true, superOnly: true },
      { label: "Prospects CRM", icon: Target, path: "/crm", adminOnly: true, superOnly: true },
      { label: "Linear TV", icon: MonitorPlay, path: "/linear-tv", adminOnly: true, superOnly: true },
    ],
  },
];

const keyOf = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-");

export default function Sidebar() {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [r2, setR2] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/storage")
      .then((res) => setR2({ enabled: res.data?.r2_enabled, bucket: res.data?.bucket }))
      .catch(() => {});
  }, [isAdmin]);

  return (
    <aside className="w-64 shrink-0 bg-[#0E131F] border-r border-gray-800/80 flex flex-col h-screen sticky top-0">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-gray-800/80">
        <img src="/streamworship-mark.png" alt="StreamWorship" className="h-9 w-9 object-contain" />
        <div className="leading-tight">
          <p className="font-display font-bold text-[15px]">Stream<span className="text-gold-gradient">Worship</span></p>
          <p className="text-[10px] text-gray-500 tracking-widest">BROADCAST CONTROL</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-6">
        {GROUPS.map((group) => {
          const items = group.items.filter((it) => (!it.adminOnly || isAdmin) && !(it.churchOnly && isAdmin) && (!it.superOnly || user?.is_super_admin));
          if (items.length === 0) return null;
          return (
            <div key={group.name}>
              <p className="px-3 mb-2 text-[10px] font-semibold text-gray-500 tracking-widest">{group.name}</p>
              <div className="space-y-0.5">
                {items.map((item) => {
                  const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      data-testid={`sidebar-link-${keyOf(item.label)}`}
                      className={
                        "group flex items-center gap-3 rounded-lg pl-3 pr-3 py-2 text-sm transition-colors " +
                        (active
                          ? "bg-amber-500/10 text-amber-400 font-medium border-l-2 border-amber-500"
                          : "text-gray-400 hover:text-white hover:bg-gray-800/50 border-l-2 border-transparent")
                      }
                    >
                      <item.icon className={"h-[18px] w-[18px] " + (active ? "text-amber-400" : "text-gray-500 group-hover:text-gray-300")} />
                      <span>{item.label}</span>
                      {item.adminOnly && <span className="ml-auto text-[9px] font-semibold text-amber-500/70 tracking-wide">ADMIN</span>}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-gray-800/80 space-y-3">
        {isAdmin && r2 && (
          <NavLink
            to="/storage"
            data-testid="sidebar-r2-status"
            title={r2.enabled ? `Connected to Cloudflare R2 (${r2.bucket})` : "R2 not configured — using local disk"}
            className={"flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-colors hover:brightness-110 " +
              (r2.enabled ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-amber-500/30 bg-amber-500/[0.06]")}
          >
            <Cloud className={"h-4 w-4 shrink-0 " + (r2.enabled ? "text-emerald-400" : "text-amber-400")} />
            <span className="min-w-0 flex-1">
              <span className={"block text-[11px] font-semibold leading-tight " + (r2.enabled ? "text-emerald-300" : "text-amber-300")}>
                {r2.enabled ? "R2: Connected" : "R2: Not configured"}
              </span>
              <span className="block text-[10px] text-gray-500 truncate leading-tight">
                {r2.enabled ? (r2.bucket || "cloud storage") : "using local disk"}
              </span>
            </span>
            <span className={"h-2 w-2 rounded-full shrink-0 " + (r2.enabled ? "bg-emerald-400 animate-live-dot" : "bg-amber-400")} />
          </NavLink>
        )}
        <div data-testid="sidebar-plan-card" className="rounded-lg bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 p-3">
          <p className="text-xs font-semibold text-amber-400">{user?.role === "admin" ? "Administrator" : ({ "basic-broadcaster": "Basic Broadcaster", "media-ministry-hub": "Media Ministry Hub", "premium-services": "Premium Services", "ascend-ultra": "Ascend Ultra" }[user?.plan] || "Free Trial")}</p>
          <NavLink to="/billing" className="text-[11px] text-gray-500 hover:text-amber-300 mt-1 block">{user?.role === "admin" ? "Manage plan prices" : "View plan & billing"}</NavLink>
        </div>
      </div>
    </aside>
  );
}
